import { useState, useEffect, useRef } from 'react';
import { Send } from 'lucide-react';

const API_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-podcast`;

export default function PodcastApplicationSection() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    alias: '',
    email: '',
    englishSpeaking: false,
    projectLink: '',
    aboutYou: '',
    aboutProject: '',
    emailConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isCollapsing, setIsCollapsing] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to submit application (${response.status})`);
      }

      setIsCollapsing(true);
      setTimeout(() => {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          alias: '',
          email: '',
          englishSpeaking: false,
          projectLink: '',
          aboutYou: '',
          aboutProject: '',
          emailConsent: false,
        });
      }, 500);
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (submitStatus === 'success' && successRef.current) {
      successRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitStatus]);

  return (
    <section id="podcast-application" className="py-20 bg-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-64 h-64 bg-purple-600 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-pink-600 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <h2 className="font-press-start text-2xl md:text-4xl text-yellow-400 text-center mb-6 leading-relaxed animate-pulse">
          Apply to Be on the Podcast
        </h2>

        <p className="text-center text-gray-300 text-lg mb-12 max-w-2xl mx-auto">
          Share your indie dev story on <span className="text-red-500 font-bold">Waiting For Players!</span> We want to hear about your journey, challenges, and what makes your game unique.
        </p>

        {submitStatus === 'success' && (
          <div
            ref={successRef}
            className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500 rounded-lg p-8 text-center animate-[slideIn_0.6s_ease-out]"
          >
            <div className="flex items-center justify-center space-x-3 mb-4">
              <svg className="w-16 h-16 text-green-400 animate-[scaleIn_0.5s_ease-out]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-3xl font-bold text-green-400 mb-3">Application Received!</h3>
            <p className="text-xl text-green-300 mb-2">Thank you for applying to be on Waiting For Players!</p>
            <p className="text-gray-300">We'll review your application and get back to you soon. Keep an eye on your inbox!</p>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className={`max-w-3xl mx-auto space-y-6 bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-600/50 rounded-lg p-8 transition-all duration-500 origin-top ${
            submitStatus === 'success'
              ? 'scale-0 opacity-0 h-0 overflow-hidden'
              : isCollapsing
              ? 'scale-y-0 opacity-0'
              : 'scale-100 opacity-100'
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-white font-bold mb-2" htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-4 py-3 bg-black/50 border border-purple-600/50 rounded text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors"
                placeholder="John"
              />
            </div>

            <div>
              <label className="block text-white font-bold mb-2" htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-4 py-3 bg-black/50 border border-purple-600/50 rounded text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors"
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <label className="block text-white font-bold mb-2" htmlFor="alias">Alias / Username</label>
            <input
              type="text"
              id="alias"
              required
              value={formData.alias}
              onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
              className="w-full px-4 py-3 bg-black/50 border border-purple-600/50 rounded text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors"
              placeholder="yOKzONa"
            />
            <p className="text-purple-300 text-sm mt-1">The name you go by in the game dev community</p>
          </div>

          <div>
            <label className="block text-white font-bold mb-2" htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-black/50 border border-purple-600/50 rounded text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors"
              placeholder="your@email.com"
            />
          </div>

          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="englishSpeaking"
              required
              checked={formData.englishSpeaking}
              onChange={(e) => setFormData({ ...formData, englishSpeaking: e.target.checked })}
              className="mt-1 w-5 h-5 bg-black/50 border border-purple-600/50 rounded focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <div>
              <label className="text-white font-bold cursor-pointer" htmlFor="englishSpeaking">English Speaking? <span className="text-red-400">*</span></label>
              <p className="text-purple-300 text-sm">Our podcast is conducted in English</p>
            </div>
          </div>

          <div>
            <label className="block text-white font-bold mb-2" htmlFor="projectLink">Project Link</label>
            <input
              type="url"
              id="projectLink"
              required
              value={formData.projectLink}
              onChange={(e) => setFormData({ ...formData, projectLink: e.target.value })}
              className="w-full px-4 py-3 bg-black/50 border border-purple-600/50 rounded text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors"
              placeholder="https://store.steampowered.com/app/..."
            />
            <p className="text-purple-300 text-sm mt-1">Steam page, itch.io, website, or any link to your game</p>
          </div>

          <div>
            <label className="block text-white font-bold mb-2" htmlFor="aboutYou">About You</label>
            <textarea
              id="aboutYou"
              required
              rows={5}
              value={formData.aboutYou}
              onChange={(e) => setFormData({ ...formData, aboutYou: e.target.value })}
              className="w-full px-4 py-3 bg-black/50 border border-purple-600/50 rounded text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors resize-none"
              placeholder="Tell us about your background, experience, and what drives you as an indie developer..."
            />
          </div>

          <div>
            <label className="block text-white font-bold mb-2" htmlFor="aboutProject">Tell Me About Your Project</label>
            <textarea
              id="aboutProject"
              required
              rows={5}
              value={formData.aboutProject}
              onChange={(e) => setFormData({ ...formData, aboutProject: e.target.value })}
              className="w-full px-4 py-3 bg-black/50 border border-purple-600/50 rounded text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-colors resize-none"
              placeholder="What's your game about? What makes it unique? What stage is it in? Share your passion and vision..."
            />
          </div>

          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="emailConsent"
              required
              checked={formData.emailConsent}
              onChange={(e) => setFormData({ ...formData, emailConsent: e.target.checked })}
              className="mt-1 w-5 h-5 bg-black/50 border border-purple-600/50 rounded focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <label className="text-white cursor-pointer" htmlFor="emailConsent">
              I agree to receive emails about news and promotions from Waiting for Players <span className="text-red-400">*</span>
            </label>
          </div>

          {submitStatus === 'error' && (
            <div className="bg-red-500/20 border-2 border-red-500 text-red-400 px-6 py-4 rounded-lg text-center animate-pulse">
              <p className="font-bold text-lg mb-1">Submission Failed</p>
              <p>Something went wrong. Please check your information and try again.</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-yellow-300 via-orange-400 via-pink-500 to-purple-600 text-black px-8 py-4 font-bold text-lg rounded hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Send size={20} />
            <span>{isSubmitting ? 'Submitting...' : 'Submit Application'}</span>
          </button>
          <p className="text-center text-purple-300 text-sm mt-4">
            We review all applications and will reach out if you're a good fit for the podcast
          </p>
        </form>
      </div>
    </section>
  );
}
